import { rankedSearch } from "@/lib/search";
// رحلة تحديد الملف الوظيفي: سؤال المنصب → جهة، أو المسار → المستوى (قوائم مختصرة بلا سلسلة هيكل)
import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { errText } from "@/v2/lib/hooks";
import { GlassCard, Chip, Skeleton } from "@/v2/components/ui";
import { Briefcase, Users, ChevronRight, Search, CheckCircle2, AlertCircle, ArrowRight } from "lucide-react";

const Step = ({ n, label, active, done }) => (
  <div className={`flex items-center gap-2 text-xs font-bold ${active ? "text-ink" : done ? "text-ok" : "text-ink-2/60"}`}>
    <span className={`w-6 h-6 rounded-full flex items-center justify-center border ${active ? "bg-beige text-ink border-beige" : done ? "border-ok" : "border-line"}`}>
      {done ? <CheckCircle2 size={14} /> : n}
    </span>
    {label}
  </div>
);

function Picker({ items, value, onPick, placeholder, testId, loading }) {
  const [q, setQ] = useState("");
  const list = rankedSearch(items || [], q, i => [i.name, i.title, i.context]);
  return (
    <div>
      <div className="relative mb-3">
        <Search size={15} className="absolute top-1/2 -translate-y-1/2 right-3 text-ink-2" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={placeholder} data-testid={`${testId}-search`}
          className="v2-input" style={{ paddingInlineStart: "2.5rem" }} />
      </div>
      {loading ? <Skeleton className="h-40 w-full" /> : (
        <div className="max-h-72 overflow-y-auto space-y-1.5 pe-1" data-testid={`${testId}-list`}>
          {list.length === 0 && <p className="text-sm text-ink-2 p-3">لا توجد نتائج مطابقة.</p>}
          {list.map((i, idx) => (
            <button key={i.id || `${i.name}-${idx}`} onClick={() => onPick(i)} data-testid={`${testId}-item`}
              className={`w-full text-start v2-inset px-4 py-3 text-sm font-semibold transition-all hover:!border-beige/50 ${value === i.id && i.id ? "!border-beige/60 !bg-beige/10" : ""}`}>
              <span className="flex items-center justify-between gap-2">
                <span>{i.name}{i.title}</span>
                {i.context && <span className="text-xs text-ink-2">{i.context}</span>}
                {i.ambiguous && <Chip tone="gold">عدة جهات</Chip>}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ProfilePicker({ onDone, compact = false }) {
  const qc = useQueryClient();
  const [isManager, setIsManager] = useState(null);
  const [position, setPosition] = useState(null);
  const [unit, setUnit] = useState(null);
  const [track, setTrack] = useState(null);
  const [trackChoice, setTrackChoice] = useState(null);
  const [level, setLevel] = useState(null);
  const [levelSearch, setLevelSearch] = useState("");
  const [ambiguous, setAmbiguous] = useState(null);
  const [busy, setBusy] = useState(false);
  const [missing, setMissing] = useState(false);
  const [missingText, setMissingText] = useState("");

  const { data: positions = [] } = useQuery({ queryKey: ["masar-positions"], queryFn: async () => (await api.get("/masar/lookups/positions")).data });
  const { data: units, isLoading: unitsLoading } = useQuery({
    queryKey: ["masar-units", position?.unit_kind],
    queryFn: async () => (await api.get(`/masar/lookups/units?kind=${position.unit_kind}`)).data,
    enabled: !!position,
  });
  const { data: tracks, isLoading: tracksLoading } = useQuery({
    queryKey: ["masar-tracks"], queryFn: async () => (await api.get("/masar/lookups/tracks")).data, enabled: isManager === false,
  });
  const { data: levels, isLoading: levelsLoading } = useQuery({
    queryKey: ["masar-levels", track?.id],
    queryFn: async () => (await api.get(`/masar/lookups/levels?track_id=${track.id}`)).data,
    enabled: !!track?.id,
  });

  useEffect(() => { setUnit(null); setAmbiguous(null); }, [position]);
  useEffect(() => { setLevel(null); }, [track]);

  const finish = async (body) => {
    setBusy(true);
    try {
      const { data } = await api.post("/masar/assign", body);
      if (data.ambiguous) { setAmbiguous(data.options); return; }
      qc.invalidateQueries({ queryKey: ["masar-me"] });
      toast.success(`تم ربط حسابك بـ «${data.profile}»`);
      onDone?.(data);
    } catch (e) {
      toast.error(errText(e));
      if (e?.response?.status === 409) onDone?.({ needs_completion: true });
    } finally { setBusy(false); }
  };

  const sendMissing = async (kind) => {
    setBusy(true);
    try {
      await api.post("/masar/completion-request", { kind, text: missingText, track_id: track?.id, unit_id: unit?.id });
      toast.success("أُرسل طلب الاستكمال للإدارة — سنبلغك عند التحديث");
      qc.invalidateQueries({ queryKey: ["masar-me"] });
      onDone?.({ needs_completion: true });
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };

  const step = isManager === null ? 1 : isManager ? (position ? 3 : 2) : (track ? 3 : 2);

  return (
    <div className="space-y-5" data-testid="profile-picker">
      {!compact && (
        <div className="flex flex-wrap gap-4">
          <Step n={1} label="طبيعة عملك" active={step === 1} done={step > 1} />
          <Step n={2} label={isManager ? "المنصب" : "الإدارة (المسار الوظيفي)"} active={step === 2} done={step > 2} />
          <Step n={3} label={isManager ? "الجهة" : "المستوى الوظيفي"} active={step === 3} done={false} />
        </div>
      )}

      {isManager === null && (
        <div className="grid sm:grid-cols-2 gap-3">
          <button onClick={() => setIsManager(true)} data-testid="picker-manager-yes" className="v2-inset p-5 text-start hover:!border-beige/50 transition-all">
            <Briefcase className="text-beige-ink mb-2" size={22} />
            <div className="font-extrabold">نعم، أشغل منصبًا إشرافيًا</div>
            <div className="text-xs text-ink-2 mt-1">نائب رئيس تنفيذي · مدير عام · مدير · رئيس قسم</div>
          </button>
          <button onClick={() => setIsManager(false)} data-testid="picker-manager-no" className="v2-inset p-5 text-start hover:!border-ok/50 transition-all">
            <Users className="text-ok mb-2" size={22} />
            <div className="font-extrabold">لا، أنا موظف في مسار وظيفي</div>
            <div className="text-xs text-ink-2 mt-1">سأختار إدارتي (المسار) ثم مستواي الوظيفي</div>
          </button>
        </div>
      )}

      {isManager === true && !position && (
        <div className="grid sm:grid-cols-2 gap-3">
          {positions.map((p) => (
            <button key={p.key} onClick={() => setPosition(p)} data-testid={`picker-position-${p.key}`} className="v2-inset p-4 text-start hover:!border-beige/50 transition-all">
              <div className="font-extrabold">{p.label}</div>
              <div className="text-xs text-ink-2 mt-1">ستختار {p.unit_kind_ar} من قائمة مختصرة</div>
            </button>
          ))}
          <button onClick={() => setIsManager(null)} className="text-xs text-ink-2 hover:text-ink flex items-center gap-1"><ArrowRight size={12} /> رجوع</button>
        </div>
      )}

      {isManager === true && position && !ambiguous && (
        <GlassCard className="p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="font-bold text-sm">اختر {position.unit_kind_ar}: <span className="v2-gold-text">{position.label}</span></div>
            <button onClick={() => setPosition(null)} className="text-xs text-ink-2 hover:text-ink">تغيير المنصب</button>
          </div>
          <Picker items={units?.items} value={unit?.id} onPick={setUnit} loading={unitsLoading} testId="picker-unit" placeholder={`ابحث في قائمة ${position.unit_kind_ar}...`} />
          <button disabled={!unit || busy} onClick={() => finish({ position_key: position.key, unit_id: unit.id })} data-testid="picker-assign-position" className="v2-btn w-full mt-4">
            <CheckCircle2 size={16} /> تأكيد: {position.label} — {unit?.name || "…"}
          </button>
        </GlassCard>
      )}

      {ambiguous && (
        <GlassCard className="p-5">
          <div className="font-bold text-sm mb-3 flex items-center gap-2"><AlertCircle size={16} className="text-beige-ink" /> يوجد أكثر من صف موثق — اختر المسمى الدقيق</div>
          <div className="space-y-2">
            {ambiguous.map((o) => (
              <button key={o.profile_id} onClick={() => finish({ position_key: position?.key, unit_id: unit?.id, profile_id: o.profile_id })} data-testid="picker-ambiguous-item" className="w-full v2-inset px-4 py-3 text-start text-sm font-semibold hover:!border-beige/50">{o.title}</button>
            ))}
          </div>
        </GlassCard>
      )}

      {isManager === false && !track && (
        <GlassCard className="p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="font-bold text-sm">اختر إدارتك (المسار الوظيفي)</div>
            <button onClick={() => setIsManager(null)} className="text-xs text-ink-2 hover:text-ink">رجوع</button>
          </div>
          {trackChoice ? (
            <div className="space-y-2">
              <div className="text-xs text-ink-2">«{trackChoice.name}» موجود في أكثر من جهة — حدد جهتك:</div>
              {trackChoice.options.map((o) => (
                <button key={o.id} onClick={() => { setTrack({ id: o.id, name: trackChoice.name }); setTrackChoice(null); }} data-testid="picker-track-context" className="w-full v2-inset px-4 py-3 text-start text-sm font-semibold hover:!border-beige/50">{o.context}</button>
              ))}
              <button onClick={() => setTrackChoice(null)} className="text-xs text-ink-2 hover:text-ink">رجوع</button>
            </div>
          ) : (
            <Picker items={tracks?.items} onPick={(t) => (t.ambiguous ? setTrackChoice(t) : setTrack(t))} loading={tracksLoading} testId="picker-track" placeholder="ابحث باسم الإدارة / المسار..." />
          )}
        </GlassCard>
      )}

      {isManager === false && track && (
        <GlassCard className="p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="font-bold text-sm">مستواك الوظيفي في <span className="v2-gold-text">{track.name}</span></div>
            <button onClick={() => { setTrack(null); setMissing(false); }} className="text-xs text-ink-2 hover:text-ink">تغيير الإدارة</button>
          </div>
          {levelsLoading ? <Skeleton className="h-32 w-full" /> : (
            <div className="space-y-1.5" data-testid="picker-level-list">
              <input value={levelSearch} onChange={e => setLevelSearch(e.target.value)} className="v2-input mb-2" aria-label="البحث في المسميات الوظيفية" placeholder="ابحث عن مسماك الوظيفي..." data-testid="picker-level-search" />
              {rankedSearch(levels?.items || [], levelSearch, l => l.title).map((l) => (
                <button key={l.profile_id} onClick={() => setLevel(l)} data-testid="picker-level-item"
                  className={`w-full text-start v2-inset px-4 py-3 text-sm font-semibold transition-all hover:!border-beige/50 ${level?.profile_id === l.profile_id ? "!border-beige/60 !bg-beige/10" : ""}`}>
                  <span className="flex items-center justify-between"><span>{l.title}</span>{l.status === "review" && <Chip tone="gold">قيد المراجعة</Chip>}</span>
                </button>
              ))}
            </div>
          )}
          {!missing ? (
            <button onClick={() => setMissing(true)} data-testid="picker-level-missing" className="text-xs text-beige-ink hover:underline mt-3 flex items-center gap-1"><AlertCircle size={12} /> مستواي الوظيفي غير موجود في القائمة</button>
          ) : (
            <div className="mt-3 v2-inset p-3">
              <label className="v2-label">اكتب مسماك الوظيفي كما هو في قرار التعيين</label>
              <input value={missingText} onChange={(e) => setMissingText(e.target.value)} data-testid="picker-missing-text" className="v2-input mb-2" />
              <button disabled={busy || missingText.trim().length < 3} onClick={() => sendMissing("missing_level")} data-testid="picker-missing-send" className="v2-btn v2-btn-sm">إرسال طلب استكمال للإدارة</button>
            </div>
          )}
          <button disabled={!level || busy} onClick={() => finish({ track_id: track.id, profile_id: level.profile_id })} data-testid="picker-assign-level" className="v2-btn w-full mt-4">
            <ChevronRight size={16} /> تأكيد: {level?.title || "اختر المستوى"}
          </button>
        </GlassCard>
      )}
    </div>
  );
}
